// Assembly.tsx — the set piece. "Two kinds of work" is a full-screen pinned
// scroll scene: the copy column (WhatWeDo) sits beside a live SVG in which
// the three solid cubes of the Blockworks mark arrive FROM THE HERO'S OWN
// CUBE CLUSTER and assemble into the logo's true arrangement, the dashed
// cube drawing its own nine edges instead of flying.
//
// The hero's mint, ice and peri cubes (Scene.tsx, [data-float]) ARE this
// scene's cubes. Each one leaves the hero at a fixed fraction of the hero's
// own scroll-through (DEPART), the top-most first, because the top of the
// hero is the first thing to scroll off; from that instant this file hides
// the hero's copy and draws its own, starting on the exact screen pixels the
// hero's copy occupied (measured live, see heroPoseOnScreen). The cube then
// HANGS at that screen position while the hero scrolls away beneath it, and
// drifts down into its slot as the stage rises to meet it, landing at a
// fixed fraction of the pin's own progress (LAND). Both ends of every flight
// are read in screen space each tick and converted into the SVG's
// coordinates, so the swap at departure is pixel-exact whatever the
// viewport, and scrolling back up returns the cube to the hero exactly.
//
// Progress through the pinned part of the scene is
// p = clamp01(-trackRect.top / (trackRect.height - viewportHeight)), a pure
// function of scroll position computed once per rAF tick in the effect
// below; the dashed cube's edge draws and the copy beats' reveals are
// derived from that single number, the flights from scrollY directly, so
// scrolling back up reverses the whole scene exactly.
//
// Below 900px (or under prefers-reduced-motion, or before JS has run) the
// SVG's own JSX default IS the finished, assembled pose — all cubes at t=1,
// every edge fully drawn and crawling — so no motion ever needs to run for
// the section to read correctly, and the hero keeps all of its own cubes.
// The effect below only overrides that default when the pin is active.
import { useEffect, useId, useRef } from 'react';
import { cubeEdges, cubeFaces, type Point } from '../../lib/iso';
import { prefersReducedMotion } from '../../motion/reducedMotion';
import { WhatWeDo } from './WhatWeDo';
import './Assembly.css';

type FlyTone = 'mint' | 'ice' | 'peri';
const FLY_TONES: FlyTone[] = ['mint', 'ice', 'peri'];

// Scattered hero origin (o), assembled logo position (to), and the scale
// ratio between the two (native altitude -> the shared assembled altitude,
// 84). Copied verbatim from the pre-assembly Scene.tsx MORPH map — Scene.tsx
// no longer needs this now that the set piece owns the assembly, so this is
// its only home, not a duplicate of a live source.
const MORPH: Record<FlyTone, { o: [number, number]; to: [number, number]; s: number }> = {
  mint: { o: [190, 470], to: [200, 430], s: 84 / 108 },
  ice: { o: [430, 150], to: [272.7, 388], s: 84 / 34 },
  peri: { o: [188, 300], to: [272.7, 304], s: 84 / 96 },
};
const NATIVE_A: Record<FlyTone, number> = { mint: 108, ice: 34, peri: 96 };

// The dashed cube never travels: it is drawn fixed at its assembled spot,
// same numbers as MORPH.dash used to carry.
const DASH_TO: [number, number] = [345.4, 430];
const DASH_A = 84;

// Fallback entry pose, in this SVG's own units, for a cube whose hero twin
// cannot be found (Scene.tsx unmounted, a markup change): above the stage,
// so it still reads as arriving from the hero's direction rather than from
// a second cast standing on the stage.
const FALLBACK_START: Record<FlyTone, { at: [number, number]; edge: number }> = {
  mint: { at: [60, -260], edge: 0.85 * DASH_A },
  ice: { at: [420, -235], edge: NATIVE_A.ice },
  peri: { at: [330, -260], edge: 0.9 * DASH_A },
};

// When each cube leaves the hero: scrollY as a fraction of the hero's own
// height. All three leave together, on the first notch of scroll: the
// cluster stays where it was while the headline scrolls away beneath it.
// Staggered departures were tried (top cube first, since the top of the
// hero scrolls off first) and read wrong — a cube that leaves later hangs
// HIGHER relative to one that left earlier, so the big mint slid up over
// the peri and hid it. Leaving as one keeps the hero's own arrangement
// intact through the hang; the stagger lives in the landings instead.
// Measured at 1440x900: the lowest cube (mint) spans 412-757px of an
// 847px hero, so at 5% every cube is still whole on screen.
const DEPART: Record<FlyTone, number> = { peri: 0.05, ice: 0.05, mint: 0.05 };

// A departing cube doesn't stop dead the instant its window opens: over
// the first DETACH_PX of scroll after departure it eases from riding with
// the page (as the hero's copy did) to hanging still on screen. A hard
// stop was a velocity kink you could see; this is a deceleration.
const DETACH_PX = 120;
function smoothstep(x: number): number {
  return x * x * (3 - 2 * x);
}

// When each cube lands: the pin's own progress p. Mint first (it carries
// the first copy beat, "Products of our own"), then peri, then the small
// ice growing into its place behind — the order the beats want, not the
// order of departure. Every flight is long (a viewport or more of scroll)
// on purpose: the cube hangs, then drifts, then settles.
const LAND: Record<FlyTone, number> = { mint: 0.15, peri: 0.25, ice: 0.35 };

// The dashed cube's own window of the pin's progress p. After it, a hold
// (p in [0.80, 0.96]) with the mark complete and still, then mintHandoff.ts
// takes the mint cube on to the Latently room from p = 0.96.
const DASH_WINDOW: [number, number] = [0.45, 0.8];

// Hand-written ease-in-out with a soft overshoot: a slow start (the cube
// hangs where the hero left it), a fast middle, a 6% overshoot past the
// slot, then the last 28% of the window spent settling back to rest — the
// "weight, overshoot, settle" read the owner wants, without the eager jump
// an ease-OUT would give the departure. Two cubic in-out segments joined at
// SPLIT; both one-sided derivatives are zero there, so the join has no
// visible kink even though it's written by hand instead of from a formula.
const OVERSHOOT = 1.06;
const SPLIT = 0.72;
function inOutCubic(u: number): number {
  return u < 0.5 ? 4 * u ** 3 : 1 - (-2 * u + 2) ** 3 / 2;
}
function easeInOutBackSoft(x: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  if (x < SPLIT) return inOutCubic(x / SPLIT) * OVERSHOOT;
  return OVERSHOOT + (1 - OVERSHOOT) * inOutCubic((x - SPLIT) / (1 - SPLIT));
}

// While a cube travels it drifts on a sine tied to its OWN raw (un-eased)
// local progress, not to time — this has to be a pure function of scroll,
// never a timer. Low cycle count so a fast scrub reads as a settle, not a
// jitter; amplitude damped by (1 - raw) so it always lands dead flush.
const BOB_CYCLES = 1.25;
const BOB_AMPLITUDE = 8;

function clamp01(v: number): number {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

function pointOf([x, y]: [number, number]): Point {
  return { x, y };
}

// The overshoot is 6% of the travel. A flight from the hero is ~600 units,
// so 6% would be a 36-unit bounce past the slot, which reads as a bounce,
// not a settle (the owner's rule: stop earlier and smaller than feels
// natural). The overshoot is capped in absolute units instead, so a long
// flight settles as tightly as a short one.
const OVERSHOOT_CAP_UNITS = 14;

// A cube's pose in this SVG's own units: where its front-bottom corner sits
// and how long its edges are. The assembled pose is MORPH[tone].to / DASH_A.
type Pose = { at: [number, number]; edge: number };

// translate+scale about the cube's own native origin (MORPH[tone].o — the
// same point CubeFacesG draws the native polygon at) that puts the drawn
// cube at `pose`. With pose = the assembled one this is exactly the old
// scattered-hero-pose formula (to - s*o, scale s), so the assembled pose is
// unchanged from before the hero flight existed.
function poseTransform(tone: FlyTone, pose: Pose, bob: number): string {
  const o = MORPH[tone].o;
  const scale = pose.edge / NATIVE_A[tone];
  const tx = pose.at[0] - scale * o[0];
  const ty = pose.at[1] - scale * o[1] + bob;
  return `translate(${tx.toFixed(2)} ${ty.toFixed(2)}) scale(${scale.toFixed(4)})`;
}

function assembledPose(tone: FlyTone): Pose {
  return { at: MORPH[tone].to, edge: DASH_A };
}

// Linear interpolation between two poses, t past 1 allowed (the easing's
// overshoot) but capped to OVERSHOOT_CAP_UNITS of travel.
function lerpPose(from: Pose, to: Pose, t: number): Pose {
  if (t > 1) {
    const travel = Math.hypot(to.at[0] - from.at[0], to.at[1] - from.at[1]) || 1;
    t = 1 + Math.min(t - 1, OVERSHOOT_CAP_UNITS / travel);
  }
  return {
    at: [from.at[0] + (to.at[0] - from.at[0]) * t, from.at[1] + (to.at[1] - from.at[1]) * t],
    edge: from.edge + (to.edge - from.edge) * t,
  };
}

const ASSEMBLED_TRANSFORM: Record<FlyTone, string> = {
  mint: poseTransform('mint', assembledPose('mint'), 0),
  ice: poseTransform('ice', assembledPose('ice'), 0),
  peri: poseTransform('peri', assembledPose('peri'), 0),
};

// The hero cube's drawn box on screen, as a pose in SCREEN pixels: its
// front-bottom corner (the box's horizontal centre, its bottom edge — the
// cube is symmetric about that corner and it is the lowest point) and its
// altitude (the box is 2 * 0.866 altitudes wide). Read from the union of
// its three face polygons, not the group's own box, so a stroke, a
// gradient def or a wrapper never widens it. Includes whatever the hero is
// doing to that cube right now — bob, pointer lean, the first-visit intro —
// which is exactly what makes the swap at departure pixel-exact.
function heroPoseOnScreen(group: Element): { ax: number; ay: number; alt: number } | null {
  let top = Infinity;
  let left = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const poly of Array.from(group.querySelectorAll('polygon'))) {
    const r = poly.getBoundingClientRect();
    top = Math.min(top, r.top);
    left = Math.min(left, r.left);
    right = Math.max(right, r.right);
    bottom = Math.max(bottom, r.bottom);
  }
  if (!Number.isFinite(top) || right <= left) return null;
  return { ax: (left + right) / 2, ay: bottom, alt: (right - left) / (2 * 0.866) };
}

// The pointer-parallax lean this SVG's own effect (below) has applied to a
// cube's data-parallax wrapper, in SVG units. CSS `translate` on an SVG <g>
// moves it in user units, and the wrapper is the only thing between the
// cube group and the <svg>, so subtracting this from a screen-derived
// position gives the wrapper-local pose the cube's own transform needs.
function wrapperLean(cubeGroup: SVGGElement): [number, number] {
  const wrapper = cubeGroup.parentElement;
  const raw = wrapper instanceof SVGGElement ? wrapper.style.translate : '';
  if (!raw) return [0, 0];
  const parts = raw.split(' ').map((v) => parseFloat(v));
  return [parts[0] || 0, parts[1] || 0];
}

// Nine edges, same coherent silhouette-then-spokes order cubeEdges always
// returns (A→B→TR→TB→TL→C→A, then the three spokes). Edge i starts once
// edge i-1 is 70% drawn: onset_i = i * OVERLAP * DURATION, chosen so that at
// local progress = onset_i, edge i-1's own progress is exactly (onset_i -
// onset_{i-1}) / DURATION = OVERLAP. DURATION is solved so the last edge
// finishes exactly at local progress 1.
const EDGE_COUNT = 9;
const EDGE_OVERLAP = 0.7;
const EDGE_DURATION = 1 / (1 + (EDGE_COUNT - 1) * EDGE_OVERLAP);
const DASH_EDGES = cubeEdges(pointOf(DASH_TO), DASH_A);
const DASH_TOP_FACE = cubeFaces(pointOf(DASH_TO), DASH_A).top;

// Face gradient stops, copied verbatim from Scene.tsx's <Grad> definitions
// (same source CubeGlyph.tsx copies its own FACE_STOPS from) — this file's
// own gradients need unique ids (via useId below), so the defs can't just
// be shared with Scene.tsx's fixed "bw-*" ids without an id collision now
// that both scenes are mounted on the page at once.
const FACE_STOPS: Record<FlyTone, { top: [string, string]; right: [string, string]; left: [string, string] }> = {
  peri: { top: ['#eef3fd', '#d3ddf5'], right: ['#c6d0ec', '#a9b7e0'], left: ['#aab8de', '#8c9cce'] },
  mint: { top: ['#ddf0f5', '#c2e2ec'], right: ['#bfe1ec', '#9ccbdc'], left: ['#a4cfdd', '#82b4c6'] },
  ice: { top: ['#f0f5fe', '#dfe9f9'], right: ['#d3e0f5', '#bccfef'], left: ['#c0cfea', '#a8bce0'] },
};

const Grad = ({ id, from, to, vertical }: { id: string; from: string; to: string; vertical?: boolean }) => (
  <linearGradient id={id} x1="0" y1="0" x2={vertical ? '0' : '1'} y2={vertical ? '1' : '0.6'}>
    <stop offset="0" stopColor={from} />
    <stop offset="1" stopColor={to} />
  </linearGradient>
);

const CubeFacesG = ({ tone, ids }: { tone: FlyTone; ids: { top: string; right: string; left: string } }) => {
  const f = cubeFaces(pointOf(MORPH[tone].o), NATIVE_A[tone]);
  return (
    <>
      <polygon points={f.left} fill={`url(#${ids.left})`} stroke="var(--cube-line)" strokeWidth="1" strokeLinejoin="round" />
      <polygon points={f.right} fill={`url(#${ids.right})`} stroke="var(--cube-line)" strokeWidth="1" strokeLinejoin="round" />
      <polygon points={f.top} fill={`url(#${ids.top})`} stroke="var(--cube-line)" strokeWidth="1" strokeLinejoin="round" />
    </>
  );
};

// The three beats' reveal windows: each completes exactly at the stated p
// (mint landed; ice landed, so ice+peri both down; dash finished drawing)
// and starts 0.08 of scroll progress earlier, so the text arrives WITH the
// cube rather than trailing it.
const BEAT_TARGETS: Record<'1' | '2' | '3', number> = { '1': LAND.mint, '2': LAND.ice, '3': DASH_WINDOW[1] };
const BEAT_SPAN = 0.08;

export function Assembly() {
  const trackRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  // useId's colon-wrapped token is a legal id but a needless risk inside a
  // url(#...) reference (Seam.tsx already strips it for the same reason).
  const uid = useId().replace(/:/g, '');
  const gradId = (tone: FlyTone, face: 'top' | 'right' | 'left') => `assembly-${uid}-${tone}-${face}`;

  useEffect(() => {
    if (prefersReducedMotion()) return; // JSX default is already the finished pose
    const track = trackRef.current;
    const stage = stageRef.current;
    const svg = svgRef.current;
    if (!track || !svg) return;

    const cubeEls: Record<FlyTone, SVGGElement | null> = {
      mint: svg.querySelector('[data-cube="mint"]'),
      ice: svg.querySelector('[data-cube="ice"]'),
      peri: svg.querySelector('[data-cube="peri"]'),
    };
    // The hero's own cubes — the source of every flight. Read by data
    // attribute (Scene.tsx's data-float groups), never by import, the same
    // decoupling the motion modules use. Missing -> FALLBACK_START.
    const landing = track.closest<HTMLElement>('.landing') ?? document.body;
    const hero = landing.querySelector<HTMLElement>('.hero');
    const heroEls: Record<FlyTone, SVGGElement | null> = {
      mint: hero?.querySelector('[data-float="mint"]') ?? null,
      ice: hero?.querySelector('[data-float="ice"]') ?? null,
      peri: hero?.querySelector('[data-float="peri"]') ?? null,
    };
    const edgeEls = Array.from(svg.querySelectorAll<SVGLineElement>('[data-edge]')).sort(
      (a, b) => Number(a.dataset.edge) - Number(b.dataset.edge)
    );
    const topFaceEl = svg.querySelector<SVGPolygonElement>('[data-dash-top]');
    const beatEls = Array.from(track.querySelectorAll<HTMLElement>('[data-beat]'));

    const pinQuery = window.matchMedia('(min-width: 900px)');

    // The pin's own progress: 0 with the stage's top at the viewport's top,
    // 1 at the end of the track.
    function computeProgress(): number {
      const rect = track!.getBoundingClientRect();
      const span = rect.height - window.innerHeight;
      if (span <= 0) return rect.top <= 0 ? 1 : 0;
      return clamp01(-rect.top / span);
    }

    // A cube's flight window in scrollY: departure at DEPART[tone] of the
    // hero's height, landing where the pin's progress reaches LAND[tone].
    // Both are live layout reads (a webfont swap or a resize moves either),
    // cheap enough to do per tick.
    function flightWindow(tone: FlyTone): [number, number] {
      const heroHeight = hero ? hero.offsetHeight : window.innerHeight;
      const rect = track!.getBoundingClientRect();
      const trackDocTop = rect.top + window.scrollY;
      const s0 = DEPART[tone] * heroHeight;
      const s1 = trackDocTop + LAND[tone] * Math.max(rect.height - window.innerHeight, 1);
      return s1 > s0 ? [s0, s1] : [s0, s0 + 1];
    }

    // Where the cube is right now, in its wrapper's own SVG units, for a
    // flight `t` of the way from the hero to the slot. Both ends are taken
    // in SCREEN space each tick: the hero twin's drawn box (as it sat on
    // screen at the departure scroll, i.e. its document position minus s0
    // — the cube HANGS there while the page moves) and the slot's screen
    // position under the current stage transform (which rises with the
    // page until the stage pins, then holds). The blend is converted back
    // through the <svg>'s screen matrix, minus the wrapper's own lean, so at
    // t = 0 the cube sits exactly on the hero's pixels whatever the
    // pointer has done to either scene, and at t = 1 it is exactly the
    // assembled pose.
    function flightPose(tone: FlyTone, t: number, s0: number): Pose {
      const cube = cubeEls[tone]!;
      const twin = heroEls[tone];
      const m = svg!.getScreenCTM();
      const onScreen = twin ? heroPoseOnScreen(twin) : null;
      if (!m || !onScreen) {
        return lerpPose(FALLBACK_START[tone], assembledPose(tone), t);
      }
      const inv = m.inverse();
      const lean = wrapperLean(cube);
      const slot = assembledPose(tone);
      // the slot, on screen, with the wrapper's lean applied (that lean is
      // part of where the assembled cube actually paints) — and as it will
      // sit once the stage is PINNED, not where it is right now. Before the
      // pin the stage is still rising with the page, and blending toward
      // its current position made every cube swoop down past its slot and
      // ride back up with the stage (measured: ~200px of climb after the
      // low point). Blending toward the pinned position instead gives one
      // monotonic drift down the screen, and the stage arrives underneath
      // exactly as the cube lands, since every landing is after the pin.
      const stageTop = stage ? Math.max(stage.getBoundingClientRect().top, 0) : 0;
      const slotScreen = new DOMPoint(slot.at[0] + lean[0], slot.at[1] + lean[1]).matrixTransform(m);
      slotScreen.y -= stageTop;
      const slotAlt = slot.edge * m.a;
      // the hero twin's hang position: where it sat on screen at departure
      // (its document position is static, so screen-at-s0 = screen-now +
      // (scrollY - s0)), reached by easing out of the page's motion over
      // DETACH_PX rather than stopping dead at s0. Read live, so the hero's
      // bob and pointer lean carry on into the hang and fade out with
      // (1 - t) as the flight progresses — and so scrolling back to s0
      // returns the cube to exactly where the hero's copy is right now.
      const since = window.scrollY - s0;
      const heroAy = onScreen.ay + since * smoothstep(clamp01(since / DETACH_PX));
      // an overshoot past the slot is capped in pixels, same 14 units
      let tt = t;
      if (tt > 1) {
        const travel = Math.hypot(slotScreen.x - onScreen.ax, slotScreen.y - heroAy) || 1;
        tt = 1 + Math.min(tt - 1, (OVERSHOOT_CAP_UNITS * m.a) / travel);
      }
      const sx = onScreen.ax + (slotScreen.x - onScreen.ax) * tt;
      const sy = heroAy + (slotScreen.y - heroAy) * tt;
      const alt = onScreen.alt + (slotAlt - onScreen.alt) * tt;
      const local = new DOMPoint(sx, sy).matrixTransform(inv);
      return { at: [local.x - lean[0], local.y - lean[1]], edge: alt / m.a };
    }

    // True after a tick in which any cube was mid-flight. While that holds
    // AND the hero is near enough for its own bob/lean loop to be running
    // (heroNear, same 200px margin Scene.tsx uses), the flight is ticked
    // every frame, not only on scroll: the hanging cube's position is read
    // live from the hero's still-moving copy, and updating it only on scroll
    // ticks would let it snap to a new bob phase after any pause. At rest
    // outside a flight nothing loops, so the idle cost stays zero.
    let inFlight = false;
    let heroNear = false;

    function applyProgress(p: number): void {
      const scrollY = window.scrollY;
      inFlight = false;
      for (const tone of FLY_TONES) {
        const g = cubeEls[tone];
        const twin = heroEls[tone];
        if (!g) continue;
        const [s0, s1] = flightWindow(tone);
        const raw = clamp01((scrollY - s0) / (s1 - s0));
        if (raw <= 0) {
          // still the hero's: this copy is hidden, the hero's shows
          g.style.visibility = 'hidden';
          if (twin) twin.style.visibility = '';
          continue;
        }
        g.style.visibility = '';
        if (twin) twin.style.visibility = 'hidden';
        if (raw >= 1) {
          g.setAttribute('transform', ASSEMBLED_TRANSFORM[tone]);
          continue;
        }
        inFlight = true;
        const eased = easeInOutBackSoft(raw);
        const bob = Math.sin(raw * Math.PI * 2 * BOB_CYCLES) * BOB_AMPLITUDE * (1 - raw);
        g.setAttribute('transform', poseTransform(tone, flightPose(tone, eased, s0), bob));
      }

      const [dw0, dw1] = DASH_WINDOW;
      const dashRaw = clamp01((p - dw0) / (dw1 - dw0));
      for (let i = 0; i < EDGE_COUNT; i++) {
        const onset = i * EDGE_OVERLAP * EDGE_DURATION;
        const edgeProgress = clamp01((dashRaw - onset) / EDGE_DURATION);
        const line = edgeEls[i];
        if (!line) continue;
        // >= 0.999, not >= 1: the last edge's schedule ends exactly at the window's
        // end, and floating point can leave it at 0.99999 forever, which kept it
        // solid after the cube had finished drawing (the owner caught it).
        if (edgeProgress >= 0.999) {
          line.classList.add('crawl');
          line.style.strokeDasharray = '';
          line.style.strokeDashoffset = '';
        } else {
          line.classList.remove('crawl');
          line.style.strokeDasharray = `${DASH_A} ${DASH_A}`;
          line.style.strokeDashoffset = String(DASH_A * (1 - edgeProgress));
        }
      }
      if (topFaceEl) {
        const op = 0.14 * clamp01((dashRaw - 0.85) / 0.15);
        topFaceEl.style.opacity = op.toFixed(3);
      }

      for (const el of beatEls) {
        const key = el.dataset.beat as '1' | '2' | '3' | undefined;
        const target = key ? BEAT_TARGETS[key] : undefined;
        if (target === undefined) continue;
        const raw = clamp01((p - (target - BEAT_SPAN)) / BEAT_SPAN);
        el.style.opacity = raw.toFixed(3);
        el.style.transform = `translateY(${(14 * (1 - raw)).toFixed(2)}px)`;
      }
    }

    function resetToDefault(): void {
      for (const tone of FLY_TONES) {
        const g = cubeEls[tone];
        const twin = heroEls[tone];
        if (g) {
          g.setAttribute('transform', ASSEMBLED_TRANSFORM[tone]);
          g.style.visibility = '';
        }
        if (twin) twin.style.visibility = '';
      }
      for (const line of edgeEls) {
        line.classList.add('crawl');
        line.style.strokeDasharray = '';
        line.style.strokeDashoffset = '';
      }
      if (topFaceEl) topFaceEl.style.opacity = '';
      for (const el of beatEls) {
        el.style.opacity = '';
        el.style.transform = '';
      }
    }

    let frameQueued = false;
    let frameHandle = 0;
    let running = false;
    let flightLoop = 0;

    function scheduleFlightLoop(): void {
      if (flightLoop || !running || !inFlight || !heroNear) return;
      flightLoop = requestAnimationFrame(() => {
        flightLoop = 0;
        if (!running) return;
        tick();
      });
    }
    function tick(): void {
      frameQueued = false;
      if (!pinQuery.matches) return; // mobile: the plain stacked layout owns this, untouched
      applyProgress(computeProgress());
      scheduleFlightLoop();
    }
    function onScrollOrResize(): void {
      if (frameQueued) return;
      frameQueued = true;
      frameHandle = requestAnimationFrame(tick);
    }
    function start(): void {
      if (running) return;
      running = true;
      window.addEventListener('scroll', onScrollOrResize, { passive: true });
      window.addEventListener('resize', onScrollOrResize);
      tick(); // land on scroll position immediately, don't wait for the next scroll
    }
    function stop(): void {
      running = false;
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (frameQueued) cancelAnimationFrame(frameHandle);
      frameQueued = false;
      if (flightLoop) cancelAnimationFrame(flightLoop);
      flightLoop = 0;
    }
    function onPinChange(): void {
      if (pinQuery.matches) {
        if (running) tick();
      } else {
        // Crossing down below 900px mid-scroll: clear every inline override
        // so the now-static mobile stack isn't left carrying a stale
        // mid-scene transform/opacity from the pinned layout — and give the
        // hero its cubes back.
        resetToDefault();
      }
    }

    // The listener runs while the hero OR the track is on screen: the first
    // flights leave the hero within its first few percent of scroll, long
    // before the track itself reaches the viewport. The hero is watched
    // with the same 200px margin Scene.tsx's own loop uses, so heroNear is
    // true for exactly as long as the hero's copy of a cube can still move.
    const intersecting = new Set<Element>();
    function onIntersect(entries: IntersectionObserverEntry[]): void {
      for (const entry of entries) {
        if (entry.isIntersecting) intersecting.add(entry.target);
        else intersecting.delete(entry.target);
        if (entry.target === hero) heroNear = entry.isIntersecting;
      }
      if (intersecting.size > 0) {
        start();
        scheduleFlightLoop();
      } else {
        stop();
      }
    }
    const io = new IntersectionObserver(onIntersect);
    io.observe(track);
    const heroIo = new IntersectionObserver(onIntersect, { rootMargin: '200px 0px 200px 0px' });
    if (hero) heroIo.observe(hero);
    pinQuery.addEventListener('change', onPinChange);
    // Land on the current scroll position at once (a refresh mid-page, or
    // the page opening at the top: the hero's cubes must not double up
    // with this scene's before the first scroll).
    tick();

    return () => {
      stop();
      io.disconnect();
      heroIo.disconnect();
      pinQuery.removeEventListener('change', onPinChange);
      resetToDefault();
    };
  }, []);

  // Pointer parallax for the four data-parallax wrappers above — the same
  // feel as the hero's own Scene.tsx (identical per-depth magnitude, the
  // same pointer-offset normalisation, and the same 0.08 chase easing),
  // just listening on .assembly__stage instead of .hero since that's the
  // element the pointer is actually over while the section is pinned. This
  // never touches the data-cube groups' own fly/draw transform, set by the
  // effect above: it only drives style.translate on their outer wrapper, so
  // the two compose. Off under reduced motion and below the 900px pin
  // breakpoint, matching this file's own pinQuery gate above.
  useEffect(() => {
    const svg = svgRef.current;
    const stage = stageRef.current;
    if (!svg || !stage) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const widthQuery = window.matchMedia('(min-width: 900px)');

    const groups = Array.from(svg.querySelectorAll<SVGGElement>('g[data-parallax]'));
    let tx = 0;
    let ty = 0;
    let cx = 0;
    let cy = 0;
    let raf = 0;

    const step = () => {
      cx += (tx - cx) * 0.08;
      cy += (ty - cy) * 0.08;
      for (const g of groups) {
        const d = Number(g.dataset.depth);
        g.style.translate = `${(cx * d).toFixed(2)}px ${(cy * d).toFixed(2)}px`;
      }
      if (Math.abs(tx - cx) > 0.001 || Math.abs(ty - cy) > 0.001) raf = requestAnimationFrame(step);
      else raf = 0;
    };
    const onMove = (e: PointerEvent) => {
      if (mq.matches || !widthQuery.matches) return;
      const r = stage.getBoundingClientRect();
      tx = (e.clientX - r.left) / r.width - 0.5;
      ty = (e.clientY - r.top) / r.height - 0.5;
      if (!raf) raf = requestAnimationFrame(step);
    };
    // Not in Scene.tsx (the hero's pointer never really "leaves" the way a
    // pinned stage's does), but here it's the difference between the cubes
    // looking alive and looking stuck off-centre after a scroll-away: home
    // immediately, same hard reset as the reduced-motion/width gates below,
    // rather than leaving a many-frame tail chasing a target that's gone.
    const onLeave = () => {
      tx = 0;
      ty = 0;
      cx = 0;
      cy = 0;
      if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
      for (const g of groups) g.style.translate = '0px 0px';
    };
    const onPref = () => {
      if (!mq.matches) return;
      tx = 0;
      ty = 0;
      cx = 0;
      cy = 0;
      for (const g of groups) g.style.translate = '0px 0px';
    };
    const onWidthChange = () => {
      if (widthQuery.matches) return;
      tx = 0;
      ty = 0;
      cx = 0;
      cy = 0;
      for (const g of groups) g.style.translate = '0px 0px';
    };

    stage.addEventListener('pointermove', onMove as EventListener, { passive: true });
    stage.addEventListener('pointerleave', onLeave as EventListener, { passive: true });
    mq.addEventListener('change', onPref);
    widthQuery.addEventListener('change', onWidthChange);
    return () => {
      stage.removeEventListener('pointermove', onMove as EventListener);
      stage.removeEventListener('pointerleave', onLeave as EventListener);
      mq.removeEventListener('change', onPref);
      widthQuery.removeEventListener('change', onWidthChange);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section className="section assembly" data-mood="work">
      <div className="assembly__track" ref={trackRef}>
        <div className="assembly__stage" ref={stageRef}>
          <div className="assembly__copy">
            <WhatWeDo />
          </div>
          <div className="assembly__scene" aria-hidden="true">
            <svg
              ref={svgRef}
              className="assembly__mark"
              viewBox="0 0 560 520"
              role="img"
              aria-label="The Blockworks mark assembling from four cubes, the fourth still being drawn"
            >
              <defs>
                {FLY_TONES.map((tone) => (
                  <g key={tone}>
                    <Grad id={gradId(tone, 'top')} from={FACE_STOPS[tone].top[0]} to={FACE_STOPS[tone].top[1]} />
                    <Grad id={gradId(tone, 'right')} from={FACE_STOPS[tone].right[0]} to={FACE_STOPS[tone].right[1]} vertical />
                    <Grad id={gradId(tone, 'left')} from={FACE_STOPS[tone].left[0]} to={FACE_STOPS[tone].left[1]} vertical />
                  </g>
                ))}
              </defs>

              {/* paint order matches Scene.tsx's own: ice, peri stacked behind
                  mint's front-left cube, dash's front-right cube on top.
                  Each data-cube group (which carries the fly/draw transform,
                  set by the effect above and left untouched by the pointer
                  effect below) gets an outer data-parallax wrapper so the
                  two transforms compose instead of fighting: SVG applies the
                  wrapper's CSS `translate` and the inner `transform`
                  attribute together, same nesting Scene.tsx already uses for
                  its own data-depth/data-float pair. Depths mirror Scene.tsx's
                  data-depth for the same tones. */}
              <g data-parallax data-depth="10">
                <g data-cube="ice" transform={ASSEMBLED_TRANSFORM.ice}>
                  <CubeFacesG tone="ice" ids={{ top: gradId('ice', 'top'), right: gradId('ice', 'right'), left: gradId('ice', 'left') }} />
                </g>
              </g>
              <g data-parallax data-depth="4">
                <g data-cube="peri" transform={ASSEMBLED_TRANSFORM.peri}>
                  <CubeFacesG tone="peri" ids={{ top: gradId('peri', 'top'), right: gradId('peri', 'right'), left: gradId('peri', 'left') }} />
                </g>
              </g>
              <g data-parallax data-depth="7">
                <g data-cube="mint" transform={ASSEMBLED_TRANSFORM.mint}>
                  <CubeFacesG tone="mint" ids={{ top: gradId('mint', 'top'), right: gradId('mint', 'right'), left: gradId('mint', 'left') }} />
                </g>
              </g>
              <g data-parallax data-depth="8">
                <g data-cube="dash">
                  <polygon data-dash-top points={DASH_TOP_FACE} fill="var(--dash-top)" opacity="0.14" />
                  {DASH_EDGES.map(([from, to], i) => (
                    <line
                      key={i}
                      data-edge={i}
                      x1={from.x}
                      y1={from.y}
                      x2={to.x}
                      y2={to.y}
                      stroke="var(--dash-edge)"
                      strokeWidth="2.5"
                      strokeDasharray="var(--seam-dash)"
                      strokeLinecap="round"
                      className="crawl"
                    />
                  ))}
                </g>
              </g>
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
}
